<template>
  <div class="loenbroek text-dark">
    <h2 class="marg-1-bottom">Lønbrøker</h2>
    <template v-if="isNoSDConfiguration">
      <div class="one-time-supplements-no-sd">
        <h2>
          <i>Skolens SD-integration er ikke konfigureret korrekt. Kontakt Docendo for at få hjælp til opsætningen -
            enten på chat eller email.</i>
        </h2>
      </div>
    </template>
    <template v-else>
      <div class="one-time-supplements__header">
        <Tabs
          class="marg-1-bottom"
          :tabs="tabsList"
          :selected="activeFilter"
          @select="setFilter"
        />
        <DoFiltersFilter
          :title="'Personer'"
          :tags="uniqueNames"
          :selectedTags="selectedNames"
          :numberOfSelectedTagsToShow="2"
          :placeholder="'Vælg personer'"
          @select="toggleName"
          @clear="clearNames">
        </DoFiltersFilter>
      </div>

      <div class="one-time-supplements__body">
        <Loader v-if="isLoading"></Loader>

        <template v-else>
          <div class="do-table__filters" v-if="activeFilter === 'draft'">
            <span class="loenbroek__info">
              Lønbrøker i udbakken sendes automatisk til lønsystemet senere på dagen. Du kan redigere eller slette dem, så længe de ligger her.
            </span>
            <button
              class="do-bttn do-bttn--submit"
              @click="createLoenbroek"
            >
              Opret lønbrøk
            </button>
          </div>
          <table class="do-table"
                 :class="{'do-table--can-edit': activeFilter === 'draft'}">
            <thead>
            <tr>
              <th
                class="th-sorter td-created"
                @click="setSort('vCreatedAt')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': !isSortPristine && sortBy==='vCreatedAt'}">
                Oprettet <span class="icon-menu-open"></span>
              </th>
              <th
                class="th-sorter"
                @click="setSort('name')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='name'}">
                Navn <span class="icon-menu-open"></span>
              </th>
              <th
                class="th-sorter"
                @click="setSort('tjenestenummer')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='tjenestenummer'}">
                Tjenestenummer <span class="icon-menu-open"></span>
              </th>
              <th
                class="th-sorter td-date"
                @click="setSort('date')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='date'}">
                Dato <span class="icon-menu-open"></span>
              </th>
              <th
                class="th-sorter"
                @click="setSort('aarstimetal')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='aarstimetal'}">
                Årsnorm <span class="icon-menu-open"></span>
              </th>
              <th
                class="th-sorter"
                @click="setSort('loendecimal')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='loendecimal'}">
                Løndecimal <span class="icon-menu-open"></span>
              </th>
              <th
                class="th-sorter"
                @click="setSort('beskaeftigelsesdecimal')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='beskaeftigelsesdecimal'}">
                Beskæftigelsesdecimal <span class="icon-menu-open"></span>
              </th>
              <th
                v-if="activeFilter === 'sent'"
                class="th-sorter"
                @click="setSort('vApprovedAt')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='vApprovedAt'}">
                Sendt til lønsystem <span class="icon-menu-open"></span>
              </th>
              <th
                v-if="activeFilter === 'sent'"
                class="th-sorter"
                @click="setSort('vApprovedBy')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='vApprovedBy'}">
                Godkendt af <span class="icon-menu-open"></span>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="displayedLoenbroeker.length === 0">
              <td :colspan="activeFilter === 'sent' ? 9 : 7" class="italic">
                Ingen lønbrøker fundet
              </td>
            </tr>
            <tr v-for="item in displayedLoenbroeker"
                :key="item.id"
                @click="setEditItem(item)">
              <td class="td-created">{{ item.vCreatedAt }}</td>
              <td>{{ item.vUserName }}</td>
              <td>{{ item.tjenestenummer }}</td>
              <td class="td-date">{{ item.vDate }}</td>
              <td>{{ item.aarstimetal || '—' }}</td>
              <td>{{ item.loendecimal != null ? formatDecimal(item.loendecimal) : '—' }}</td>
              <td>{{ item.beskaeftigelsesdecimal != null ? formatDecimal(item.beskaeftigelsesdecimal) : '—' }}</td>
              <td v-if="activeFilter === 'sent'">{{ item.vApprovedAt }}</td>
              <td v-if="activeFilter === 'sent'">{{ item.vApprovedBy }}</td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
    </template>
    <LoenbroekModal
      v-if="isShowLoenbroekModal"
      :theEditItem="theEditItem"
      @close="isShowLoenbroekModal = false"
      @created="onCreated"
      @deleted="onDeleted"
    />
  </div>
</template>

<script>
import docendo from '../../../docendo'
import LoenbroekModal from './LoenbroekModal.vue'
import Loader from '../../common/Loader.vue'
import Tabs from '../../common/Tabs.vue'
import moment from 'moment'
import DoFiltersFilter from '../../DoFilters/DoFiltersFilter.vue'

export default {
  name: 'Loenbroek',
  components: {LoenbroekModal, Loader, Tabs, DoFiltersFilter},
  data () {
    return {
      isNoSDConfiguration: false,
      isShowLoenbroekModal: false,
      loenbroeker: [],
      isLoading: false,
      activeFilter: 'draft',
      sortBy: 'vCreatedAt',
      sortDir: 'desc',
      theEditItem: null,
      isSortPristine: true,
      selectedNames: []
    }
  },
  computed: {
    schoolId () {
      return this.$store.getters.schoolId
    },
    usersById () {
      const map = {}
      const list = this.$store.getters.usersSortedOnName || []
      for (let i = 0; i < list.length; i++) {
        const u = list[i]
        map[u.id] = u.name
      }
      return map
    },
    activeStatesForFilter () {
      if (this.activeFilter === 'sent') return ['sent', 'sending']
      return [this.activeFilter]
    },
    uniqueNames () {
      const set = {}
      const states = this.activeStatesForFilter
      for (let i = 0; i < this.loenbroekerSorted.length; i++) {
        const s = this.loenbroekerSorted[i]
        if (states.indexOf(s.state) < 0) continue
        const name = s.vUserName || ''
        if (name) set[name] = true
      }
      const arr = Object.keys(set)
      return arr.sort((a, b) => a.localeCompare(b, 'da', {sensitivity: 'base'}))
    },
    loenbroekerSorted () {
      return this.loenbroeker.map(l => {
        const vUserName = this.getUserName(l.user_id)
        const vCreatedAt = l.created_at ? moment(l.created_at).format('lll') : ''
        const vApprovedAt = l.approved_at ? moment(l.approved_at).format('lll') : ''
        const vApprovedBy = this.getUserName(l.approved_by)
        const vDate = l.dato ? moment(l.dato).format('DD-MM-YYYY') : ''
        return Object.assign({}, l, {
          vUserName, vCreatedAt, vApprovedAt, vApprovedBy, vDate
        })
      })
    },
    displayedLoenbroeker () {
      const states = this.activeStatesForFilter
      let rows = this.loenbroekerSorted.filter(x => states.indexOf(x.state) >= 0)

      // Filtrer på navne
      if (this.selectedNames && this.selectedNames.length > 0) {
        const chosen = {}
        for (let i = 0; i < this.selectedNames.length; i++) chosen[this.selectedNames[i]] = true
        rows = rows.filter(x => chosen[x.vUserName || ''])
      }

      const get = (item, key) => {
        switch (key) {
          case 'name':
            return item.vUserName || ''
          case 'date':
            return this._dateForSort(item.dato)
          case 'tjenestenummer':
            return item.tjenestenummer || 0
          case 'aarstimetal':
            return item.aarstimetal || 0
          case 'loendecimal':
            return item.loendecimal || 0
          case 'beskaeftigelsesdecimal':
            return item.beskaeftigelsesdecimal || 0
          case 'vCreatedAt':
            return this._momentOrNull(item.created_at).valueOf() || 0
          case 'vApprovedAt':
            return this._momentOrNull(item.approved_at).valueOf() || 0
          case 'vApprovedBy':
            return String(item.vApprovedBy || '')
          default:
            return item[key] || ''
        }
      }

      const dir = this.sortDir === 'asc' ? 1 : -1
      const key = this.sortBy

      return rows.slice().sort((a, b) => {
        const va = get(a, key)
        const vb = get(b, key)
        if (va === vb) return 0
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
        if (va instanceof Date && vb instanceof Date) return (va - vb) * dir
        if (typeof va === 'number' && typeof vb !== 'number') return -1 * dir
        if (typeof vb === 'number' && typeof va !== 'number') return 1 * dir
        return String(va).localeCompare(String(vb), 'da', {sensitivity: 'base'}) * dir
      })
    },
    statesPresent () {
      const set = {}
      for (let i = 0; i < this.loenbroekerSorted.length; i++) {
        const s = this.loenbroekerSorted[i]
        const st = s && s.state ? String(s.state) : 'unknown'
        set[st] = true
      }
      return Object.keys(set)
    },
    countsByState () {
      const counts = {}
      for (let i = 0; i < this.loenbroekerSorted.length; i++) {
        const st = this.loenbroekerSorted[i] && this.loenbroekerSorted[i].state
          ? String(this.loenbroekerSorted[i].state)
          : 'unknown'
        counts[st] = (counts[st] || 0) + 1
      }
      return counts
    },
    tabsList () {
      const labels = {
        draft: 'Udbakke',
        sent: 'Sendt til lønsystem',
        error: 'Fejl',
        unknown: 'Ukendt'
      }

      const counts = Object.assign({}, this.countsByState)
      if (counts.sending && counts.sending > 0) {
        counts.sent = (counts.sent || 0) + counts.sending
        delete counts.sending
      }

      const all = this.statesPresent
      const extras = []
      for (let i = 0; i < all.length; i++) {
        const st = all[i]
        if (st !== 'draft' && st !== 'sent' && st !== 'sending') extras.push(st)
      }

      const pref = ['error', 'unknown']
      const extrasKnown = []
      for (let i = 0; i < pref.length; i++) {
        const st = pref[i]
        if (extras.indexOf(st) >= 0) extrasKnown.push(st)
      }
      const extrasOther = []
      for (let j = 0; j < extras.length; j++) {
        const st = extras[j]
        if (pref.indexOf(st) < 0) extrasOther.push(st)
      }
      extrasOther.sort((a, b) => a.localeCompare(b, 'da', {sensitivity: 'base'}))

      const out = []

      out.push({
        id: 'draft',
        title: (labels['draft'] || this._capitalize('draft')) + ' (' + String(counts['draft'] || 0) + ')'
      })

      const orderedExtras = extrasKnown.concat(extrasOther)
      for (let k = 0; k < orderedExtras.length; k++) {
        const st = orderedExtras[k]
        const title = (labels[st] || this._capitalize(st)) + ' (' + String(counts[st] || 0) + ')'
        out.push({id: st, title})
      }

      out.push({
        id: 'sent',
        title: (labels['sent'] || this._capitalize('sent')) + ' (' + String(counts['sent'] || 0) + ')'
      })

      return out
    }
  },
  methods: {
    createLoenbroek () {
      this.theEditItem = null
      this.isShowLoenbroekModal = true
    },
    readLoenbroeker () {
      this.isLoading = true
      const success = (res) => {
        if (res.status && res.status === 'error') {
          this.isNoSDConfiguration = true
        }
        if (Array.isArray(res)) {
          this.loenbroeker = res
        } else if (res && Array.isArray(res.data)) {
          this.loenbroeker = res.data
        } else {
          this.loenbroeker = []
        }
        this.isLoading = false
      }
      const failure = () => {
        this.isLoading = false
        this.loenbroeker = []
      }
      docendo.listSilkeborgDataLoenaendringer(this.schoolId, success, failure)
    },
    onCreated (data) {
      if (!data || !data.id) return
      const idx = this.loenbroeker.findIndex(loenbroek => loenbroek.id === data.id)
      if (idx < 0) {
        this.loenbroeker.push(data)
      } else {
        this.$set(this.loenbroeker, idx, Object.assign({}, this.loenbroeker[idx], data))
      }
      this.isShowLoenbroekModal = false
      this.theEditItem = null
    },
    onDeleted (data) {
      if (!data || !data.id) return
      const idx = this.loenbroeker.findIndex(loenbroek => loenbroek.id === data.id)
      if (idx >= 0) {
        this.loenbroeker.splice(idx, 1)
      }
      this.isShowLoenbroekModal = false
      this.theEditItem = null
    },
    setFilter (id) {
      this.activeFilter = id
      this.readLoenbroeker()
    },
    toggleName (name) {
      const idx = this.selectedNames.indexOf(name)
      if (idx >= 0) {
        this.selectedNames.splice(idx, 1)
      } else {
        this.selectedNames.push(name)
      }
    },
    clearNames () {
      this.selectedNames = []
    },
    setEditItem (item) {
      if (this.activeFilter !== 'draft') {
        return
      }
      this.theEditItem = item
      if (item) {
        this.isShowLoenbroekModal = true
      }
    },
    setSort (key) {
      this.isSortPristine = false
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = key
        this.sortDir = 'asc'
      }
    },
    _momentOrNull (iso) {
      return iso ? moment(iso) : null
    },
    _dateForSort (dato) {
      if (!dato) return new Date(0)
      const m = moment(dato, 'YYYY-MM-DD')
      return m.isValid() ? m.toDate() : new Date(0)
    },
    _capitalize (str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    getUserName (userId) {
      if (!userId) {
        return ''
      }
      const user = this.usersById[userId]
      if (user) {
        return user
      }
      return userId
    },
    formatDecimal (value) {
      if (value == null || value === '') return ''
      return String(value).replace('.', ',')
    }
  },
  mounted () {
    this.readLoenbroeker()
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.loenbroek {
  padding: 1rem;
}

.loenbroek__info {
  font-size: 0.85rem;
  color: #4a5568;
  font-style: italic;
}

.one-time-supplements__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.one-time-supplements__header .tabs-list {
  margin-bottom: 0 !important;
  height: 26px;
}

.do-table tbody tr:hover {
  background-color: aliceblue !important;
}

.do-table tr:nth-child(2n) {
  background-color: #f9fafb;
}

.do-table td {
  border-left: 1px solid #e2e8f0;
}

.do-table.do-table--can-edit tbody tr {
  cursor: pointer;
}

.th-sorter {
  cursor: pointer;
}

.th-sorter:hover {
  transition: 0.15s;
}

.th-sorter:active {
  transform: scale(0.98);
}

.th-sorter.th-sorter--active {
  color: var(--primary);
}

.th-check, .td-check {
  width: 36px;
  text-align: center;
  cursor: pointer;
  user-select: none;
}

.th-check:hover, .td-check:hover {
  background-color: rgba(0, 0, 0, 0.05)
}

.check-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.th-check input[type="checkbox"],
.td-check input[type="checkbox"] {
  pointer-events: none;
}

.send-button-container {
  display: flex;
  align-items: center;
}

.send-button-container button {
  margin-right: 1rem;
}

.do-bttn--disabled {
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.5;
}

.do-table__filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f6f9fc;
  border: 1px solid #e2e8f0;
  border-bottom: 0;
  padding: 8px;
  border-radius: 4px 4px 0 0;
}

.td-date {
  width: 100px;
}

.td-created {
  width: 150px;
}

.marg-1-bottom {
  margin-bottom: 1rem;
}

.italic {
  font-style: italic;
}

</style>



// WEBPACK FOOTER //
// Loenbroek.vue?1d92bb2b